import { CovSensorStatus, SoundSensorStatus, type FrostFreeStatus } from "@eisox/api/core-api";

import type { EnhancedRoom } from "~/types/room.type";

export interface ParametersFilters {
  unusualAbsenceDetection?: boolean;
  precomfortRangeGeneration?: boolean;
  buttonSettings?: boolean;
  frostFreeMode?: FrostFreeStatus;
  comfortTemperature?: number;
  nightTemperature?: number;
  soundSensorParam?: boolean;
  covSensorParam?: CovSensorStatus;
}

export interface Filters {
  textSearch?: string;
  types?: string[];
  plans?: string[];
  groups?: string[];
  parameters?: ParametersFilters;
  personalizedRoomIds?: string[];
}

export const filterRooms = (rooms: EnhancedRoom[], filters: Filters): EnhancedRoom[] => {
  const { textSearch, types, plans, groups, parameters, personalizedRoomIds } = filters;

  // If no filter are selected, return no rooms
  if (
    (textSearch === undefined || textSearch === "") &&
    (types === undefined || types.length === 0) &&
    (plans === undefined || plans.length === 0) &&
    (groups === undefined || groups.length === 0) &&
    (!parameters || Object.values(parameters).every(value => value === undefined)) &&
    (personalizedRoomIds === undefined || personalizedRoomIds.length === 0)
  ) {
    return [];
  }

  if (personalizedRoomIds && personalizedRoomIds.length > 0)
    return rooms.filter(room => personalizedRoomIds.includes(room.id));

  let filteredRooms = [...rooms];

  if (textSearch) {
    filteredRooms = filterByTextSearch(textSearch, filteredRooms);
  }

  if (types && types.length > 0) {
    filteredRooms = filterByType(types, filteredRooms);
  }

  if (plans && plans.length > 0) {
    filteredRooms = filterByPlan(plans, filteredRooms);
  }

  if (groups && groups.length > 0) {
    filteredRooms = filterByGroup(groups, filteredRooms);
  }

  filteredRooms = filterByParameters(filteredRooms, parameters);

  return filteredRooms;
};

const filterByTextSearch = (textSearch: string, rooms: EnhancedRoom[]): EnhancedRoom[] =>
  rooms.filter(
    room =>
      room.name.toLowerCase().includes(textSearch.toLowerCase()) ||
      room.floor.name.toLowerCase().includes(textSearch.toLowerCase()),
  );

const filterByType = (types: string[], rooms: EnhancedRoom[]): EnhancedRoom[] =>
  rooms.filter(room => types.includes(room.type));

const filterByPlan = (plans: string[], rooms: EnhancedRoom[]): EnhancedRoom[] =>
  rooms.filter(room => plans.includes(room.floor.id));

const filterByGroup = (groups: string[], rooms: EnhancedRoom[]): EnhancedRoom[] =>
  rooms.filter(room => room.groups.some(group => groups.includes(group)));

const filterByParameters = (rooms: EnhancedRoom[], parameters?: ParametersFilters): EnhancedRoom[] => {
  const {
    unusualAbsenceDetection,
    precomfortRangeGeneration,
    buttonSettings,
    frostFreeMode,
    comfortTemperature,
    nightTemperature,
    soundSensorParam,
    covSensorParam,
  } = parameters ?? {};

  let filteredRooms = [...rooms];

  if (unusualAbsenceDetection !== undefined) {
    filteredRooms = filteredRooms.filter(room => room.isAutoPrecomf === unusualAbsenceDetection);
  }
  if (precomfortRangeGeneration !== undefined) {
    filteredRooms = filteredRooms.filter(room => room.isPreComfRoom === precomfortRangeGeneration);
  }
  if (buttonSettings !== undefined) {
    filteredRooms = filteredRooms.filter(room => room.isSwitchEnabled === buttonSettings);
  }
  if (frostFreeMode !== undefined) {
    // oxlint-disable-next-line typescript/no-unnecessary-condition -- runtime API guard despite OpenAPI typing
    filteredRooms = filteredRooms.filter(room => room.frostFree?.status === frostFreeMode);
  }
  if (comfortTemperature !== undefined) {
    filteredRooms = filteredRooms.filter(room => room.comfortTemperature === comfortTemperature);
  }
  if (nightTemperature !== undefined) {
    filteredRooms = filteredRooms.filter(room => room.nightTemperature === nightTemperature);
  }
  if (soundSensorParam !== undefined) {
    filteredRooms = filteredRooms.filter(
      room =>
        // oxlint-disable-next-line typescript/no-unnecessary-condition -- runtime API guard despite OpenAPI typing
        room.soundSensorParam?.status === (soundSensorParam ? SoundSensorStatus.ENABLED : SoundSensorStatus.DISABLED),
    );
  }
  if (covSensorParam !== undefined) {
    // oxlint-disable-next-line typescript/no-unnecessary-condition -- runtime API guard despite OpenAPI typing
    filteredRooms = filteredRooms.filter(room => room.covSensorParam?.status === covSensorParam);
  }
  return filteredRooms;
};
